import { useCallback, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  Button,
  InformationalScreen,
  LoadingModal,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import {
  Footer,
  FormContainer,
  Input,
  MainContainer,
  TopHeaderBackArrow,
} from 'Components/utility';
import { ApiStatus, ErrorCode } from 'Constants';
import { type FormikHelpers, useFormik } from 'formik';
import { useApiError, useAuth } from 'Hooks';
import { forgotPass } from 'Services/auth';
import { type RootState } from 'src/reducers';
import i18n from 'Utilities/i18n';
import * as log from 'Utilities/log';
import mediator from 'Utilities/mediator';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { notifyApp } from 'Utilities/appCommunicator';

const initialValuesFormik = { email: '' };

type FormValues = typeof initialValuesFormik;

const mapStateToProps = ({ env: { osType, osVersion, app, appVersion } }: RootState) => ({
  osType,
  osVersion,
  app,
  appVersion,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const ForgotPasswordPage = ({ osType, osVersion, app, appVersion }: Props) => {
  const validationSchema = z.object({
    email: z
      .string(i18n.t('ForgotPassword.content.forgotPasswordForm.formField.email.errors.required'))
      .min(1, i18n.t('ForgotPassword.content.forgotPasswordForm.formField.email.errors.required'))
      .email(
        i18n.t(
          'ForgotPassword.content.forgotPasswordForm.formField.email.errors.emailAddressValidEmail'
        )
      ),
  });

  const [pageIndex, setPageIndex] = useState(0);
  const { showApiErrorModal, showNetworkErrorModal } = useApiError();
  const { showHcpError, showForgotPasswordBlockedMessage, showForgotPasswordConfirmationMessage } =
    useAuth();

  const handleApiRequestError = useCallback(
    (error: AxiosError<ApiErrorData>) => {
      switch (error.status) {
        case ApiStatus.FORBIDDEN: {
          const code = error.response?.data.code;

          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showHcpError();
          }

          break;
        }

        case ApiStatus.TOO_MANY_REQUESTS:
          showForgotPasswordBlockedMessage();
          break;
        default: {
          if (error.code === ErrorCode.NETWORK_ERROR) {
            showNetworkErrorModal();
          } else {
            showApiErrorModal();
          }
        }
      }
    },
    [showApiErrorModal, showForgotPasswordBlockedMessage, showHcpError, showNetworkErrorModal]
  );

  const handleSubmitForgotPass = useCallback(
    (email: string) => {
      return new Promise<void>((resolve, reject) => {
        forgotPass(email, osType, osVersion, app, appVersion)
          .then(resolve)
          .catch((error: AxiosError<ApiErrorData>) => {
            handleApiRequestError(error);
            reject(error);
          });
      });
    },
    [osType, osVersion, app, appVersion, handleApiRequestError]
  );

  const onSubmitForm = useCallback(
    async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
      setSubmitting(true);

      try {
        await handleSubmitForgotPass(values.email);
        setPageIndex(1);
      } catch (err) {
        log.error(err);
      } finally {
        setSubmitting(false);
      }
    },
    [handleSubmitForgotPass]
  );

  const { handleSubmit, handleChange, handleBlur, isValid, isSubmitting, errors, touched, values } =
    useFormik({
      initialValues: initialValuesFormik,
      validationSchema: toFormikValidationSchema(validationSchema),
      onSubmit: onSubmitForm,
      validateOnBlur: true,
    });

  const handleResendEmail = () => {
    handleSubmitForgotPass(values.email)
      .then(showForgotPasswordConfirmationMessage)
      .catch((err) => {
        log.error(err);
      });
  };

  const handleClickDone = () => {
    mediator.publish('router:navigate', '/login');
  };

  const handleClickBack = () => {
    if (pageIndex === 1) {
      setPageIndex(0);

      return;
    }

    mediator.publish('router:back');
    notifyApp('adc-webview:navigate-back-app');
  };

  return (
    <MainContainer>
      {isSubmitting && <LoadingModal aria-label="Loading content" />}
      <InformationalScreen
        index={pageIndex}
        topNavigationProps={{
          title: i18n.t('ForgotPassword.title'),
          animatedTitle: true,
          buttonStart: {
            element: <TopHeaderBackArrow testID="ForgotPass.step1-go-back" />,
            label: 'ForgotPass.step1-go-back',
            onPress: handleClickBack,
          },
          testID: 'ForgotPass.forgotPassword',
        }}
      >
        <InformationalScreen.Page>
          <InformationalScreen.Content>
            <Text testID="ForgotPassword.subtitle">{i18n.t('ForgotPassword.subtitle')}</Text>
            <FormContainer>
              <Input
                aria-label={i18n.t(
                  'ForgotPassword.content.forgotPasswordForm.formField.email.label'
                )}
                placeholder={i18n.t(
                  'ForgotPassword.content.forgotPasswordForm.formField.email.placeholder'
                )}
                label={i18n.t('ForgotPassword.content.forgotPasswordForm.formField.email.label')}
                errorMessage={errors.email}
                isInvalid={Boolean(errors.email && touched.email)}
                testID="email"
                value={values.email}
                marginTop="$4"
                onChangeText={handleChange('email')}
                onBlur={handleBlur('email')}
                inputMode="email"
              />
            </FormContainer>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Footer
              buttonLabel={i18n.t(
                'ForgotPassword.content.forgotPasswordForm.links.sendToEmail.label'
              )}
              onButtonPress={handleSubmit}
              buttonTestID="ForgotPass.sendLinkToEmail"
              disabled={!values.email || !isValid || isSubmitting}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>

        <InformationalScreen.Page>
          <InformationalScreen.Content>
            <Text testID="ForgotPass.instructionsResetSendTo">
              {i18n.t('CheckYourEmail.subtitle')}
            </Text>
            <Text type="$bodyBase.strong" testID="ForgotPassword.user-email">
              {values.email}
            </Text>
            <img
              src={i18n.t('CheckYourEmail.content.checkYourEmail.media.checkYourEmail')}
              alt=""
              style={{
                objectFit: 'contain',
                height: 190,
              }}
              id="ForgotPassword.check-email-img"
            />
            <Stack gap={6}>
              <Text marginTop="$4" textAlign="center" testID="ForgotPassword.notReceiveEmail">
                {i18n.t('CheckYourEmail.content.checkYourEmail.primaryText')}
              </Text>
              <Stack flexDirection="row" justifyContent="center">
                <Button
                  variant="tertiary"
                  label={i18n.t('CheckYourEmail.content.checkYourEmail.links.resendEmail.label')}
                  aria-label={i18n.t(
                    'CheckYourEmail.content.checkYourEmail.links.resendEmail.label'
                  )}
                  onPress={handleResendEmail}
                  testID="ForgotPassword.resendEmail-btn"
                />
              </Stack>
            </Stack>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Footer
              buttonLabel={i18n.t('CheckYourEmail.content.checkYourEmail.links.done.label')}
              onButtonPress={handleClickDone}
              buttonTestID="ForgotPassword.done-btn"
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(ForgotPasswordPage);
